import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Dealer Performance Analytics
 *
 * Track individual dealer performance metrics:
 * - Memo statistics and conversion rates
 * - Diamond preferences and trends
 * - Response times and activity patterns
 * - Comparative performance metrics
 */
import { useState, useEffect } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../utils/apiClient';
import { MemoStatus } from '../../types/memo';
import { FaUser, FaFileContract, FaCheckCircle, FaTimesCircle, FaDollarSign, FaClock, FaTrophy, FaChartLine } from 'react-icons/fa';
import { PageContainer, PageTitle, Text, SectionTitle, ContentSection } from '../../components/PageStyles';
import styled from 'styled-components';
// Styled Components
const StatsGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
`;
const StatCard = styled.div `
  background: var(--bg-secondary);
  padding: 24px;
  border: 2px solid ${props => props.$highlight ? '#10b981' : 'var(--border-color)'};
  text-align: center;
  transition: all 0.2s;

  &:hover {
    border-color: var(--accent-color);
    transform: translateY(-2px);
  }

  .stat-value {
    font-size: 36px;
    font-weight: bold;
    color: ${props => props.$highlight ? '#10b981' : 'var(--text-primary)'};
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    svg {
      font-size: 28px;
    }
  }

  .stat-label {
    font-size: 12px;
    color: var(--text-tertiary);
    text-transform: uppercase;
    letter-spacing: 1px;
  }

  .stat-sublabel {
    font-size: 11px;
    color: var(--text-secondary);
    margin-top: 4px;
  }
`;
const DealerSelector = styled.div `
  margin-bottom: 32px;
  display: flex;
  gap: 16px;
  align-items: center;

  label {
    font-weight: 600;
    color: var(--text-primary);
  }

  select {
    flex: 1;
    max-width: 400px;
    padding: 12px 16px;
    background: var(--bg-secondary);
    border: 2px solid var(--border-color);
    color: var(--text-primary);
    font-size: 14px;
    cursor: pointer;
    transition: all 0.2s;

    &:focus {
      outline: none;
      border-color: var(--accent-color);
    }
  }
`;
const ChartCard = styled.div `
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  padding: 24px;
  margin-bottom: 24px;
`;
const BarChart = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
const Bar = styled.div `
  display: flex;
  align-items: center;
  gap: 12px;

  .label {
    width: 150px;
    min-width: 150px;
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 600;
    text-transform: capitalize;
  }

  .bar-container {
    flex: 1;
    height: 36px;
    background: var(--bg-tertiary);
    border-radius: 4px;
    overflow: hidden;
    position: relative;
  }

  .bar-fill {
    height: 100%;
    width: ${props => props.$percentage}%;
    background: ${props => props.$color};
    transition: width 0.5s ease;
    display: flex;
    align-items: center;
    padding-left: 12px;
    color: white;
    font-weight: bold;
    font-size: 13px;
  }

  .count {
    min-width: 80px;
    text-align: right;
    color: var(--text-secondary);
    font-size: 14px;
    font-weight: 600;
  }
`;
const TimelineCard = styled.div `
  background: var(--bg-secondary);
  border: 2px solid var(--border-color);
  padding: 24px;
`;
const TimelineItem = styled.div `
  padding: 16px;
  border-left: 3px solid var(--accent-color);
  margin-left: 12px;
  padding-left: 24px;
  margin-bottom: 16px;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    left: -9px;
    top: 20px;
    width: 15px;
    height: 15px;
    background: var(--accent-color);
    border-radius: 50%;
    border: 3px solid var(--bg-secondary);
  }

  .timeline-date {
    font-size: 12px;
    color: var(--text-tertiary);
    margin-bottom: 4px;
  }

  .timeline-text {
    color: var(--text-primary);
    font-size: 14px;
    font-weight: 500;
  }

  .timeline-detail {
    color: var(--text-secondary);
    font-size: 13px;
    margin-top: 4px;
  }
`;
const DealerAnalytics = () => {
    const { user } = useAuthStore();
    const [dealers, setDealers] = useState([]);
    const [selectedDealerId, setSelectedDealerId] = useState('');
    const [stats, setStats] = useState({
        totalMemos: 0,
        approvedMemos: 0,
        rejectedMemos: 0,
        soldMemos: 0,
        returnedMemos: 0,
        approvalRate: 0,
        conversionRate: 0,
        avgMemoValue: 0,
        totalValue: 0,
        avgResponseTime: 0,
        overdueCount: 0,
        shapePreferences: {},
        colorPreferences: {},
        clarityPreferences: {},
        recentActivity: []
    });
    const [loading, setLoading] = useState(false);
    const isAdmin = user && user.roleLevel >= 7;
    useEffect(() => {
        if (isAdmin) {
            loadDealers();
        }
        else if (user) {
            // For non-admin users, load their own stats
            setSelectedDealerId(user.id);
        }
    }, [user, isAdmin]);
    useEffect(() => {
        if (selectedDealerId) {
            loadDealerAnalytics(selectedDealerId);
        }
    }, [selectedDealerId]);
    const loadDealers = async () => {
        try {
            const res = await apiClient.get('users-list');
            if (res.ok && res.data?.users) {
                // Filter to dealers only (roleLevel >= 3)
                const dealerUsers = res.data.users
                    .filter((u) => u.roleLevel >= 3 && u.status === 'active')
                    .map((u) => ({
                    id: u.id,
                    email: u.email,
                    name: u.profile?.businessName || u.email
                }));
                setDealers(dealerUsers);
                if (dealerUsers.length > 0) {
                    setSelectedDealerId(dealerUsers[0].id);
                }
            }
        }
        catch (error) {
            console.error('Failed to load dealers:', error);
        }
    };
    const loadDealerAnalytics = async (dealerId) => {
        setLoading(true);
        try {
            // Fetch memos - use different endpoint based on role
            let memosRes;
            if (isAdmin) {
                // Admin: fetch all memos with dealer filter
                memosRes = await apiClient.get(`memos/all?dealerId=${dealerId}`);
            }
            else {
                // Non-admin: fetch only their own memos
                memosRes = await apiClient.get(`memos/my-requests`);
            }
            if (!memosRes.ok || !memosRes.data?.memos) {
                setLoading(false);
                return;
            }
            const dealerMemos = memosRes.data.memos;
            // Calculate statistics
            const total = dealerMemos.length;
            const approved = dealerMemos.filter(m => m.approvedAt).length;
            const rejected = dealerMemos.filter(m => m.status === MemoStatus.REJECTED).length;
            const sold = dealerMemos.filter(m => m.status === MemoStatus.SOLD).length;
            const returned = dealerMemos.filter(m => m.status === MemoStatus.RETURNED).length;
            const approvalRate = total > 0 ? (approved / total) * 100 : 0;
            const completedMemos = sold + returned;
            const conversionRate = completedMemos > 0 ? (sold / completedMemos) * 100 : 0;
            // Value calculations
            const totalValue = dealerMemos.reduce((sum, m) => sum + (m.diamondDetails?.priceUSD || 0), 0);
            const avgValue = total > 0 ? totalValue / total : 0;
            // Response time calculation (approved to returned/sold)
            let totalDays = 0;
            let completedCount = 0;
            dealerMemos.forEach(m => {
                if (m.approvedAt && (m.returnedAt || m.status === MemoStatus.SOLD)) {
                    const approved = new Date(m.approvedAt);
                    const completed = new Date(m.returnedAt || m.updatedAt);
                    const days = (completed.getTime() - approved.getTime()) / (1000 * 60 * 60 * 24);
                    totalDays += days;
                    completedCount++;
                }
            });
            const avgResponseTime = completedCount > 0 ? totalDays / completedCount : 0;
            // Overdue count
            const now = new Date();
            const overdue = dealerMemos.filter(m => {
                if (m.dueDate && [MemoStatus.SHIPPED, MemoStatus.WITH_CLIENT].includes(m.status)) {
                    return new Date(m.dueDate) < now;
                }
                return false;
            }).length;
            // Diamond preferences
            const shapes = {};
            const colors = {};
            const clarities = {};
            dealerMemos.forEach(m => {
                const d = m.diamondDetails;
                if (d) {
                    shapes[d.shape] = (shapes[d.shape] || 0) + 1;
                    colors[d.color] = (colors[d.color] || 0) + 1;
                    clarities[d.clarity] = (clarities[d.clarity] || 0) + 1;
                }
            });
            // Recent activity (last 10 memos)
            const recentActivity = dealerMemos
                .slice(0, 10)
                .map(m => ({
                date: m.requestedAt,
                action: m.status,
                diamond: `${m.diamondDetails.shape} ${m.diamondDetails.caratWeight}ct ${m.diamondDetails.color} ${m.diamondDetails.clarity}`,
                value: m.diamondDetails.priceUSD
            }));
            setStats({
                totalMemos: total,
                approvedMemos: approved,
                rejectedMemos: rejected,
                soldMemos: sold,
                returnedMemos: returned,
                approvalRate,
                conversionRate,
                avgMemoValue: avgValue,
                totalValue,
                avgResponseTime,
                overdueCount: overdue,
                shapePreferences: shapes,
                colorPreferences: colors,
                clarityPreferences: clarities,
                recentActivity
            });
        }
        catch (error) {
            console.error('Failed to load dealer analytics:', error);
        }
        finally {
            setLoading(false);
        }
    };
    const topShapes = Object.entries(stats.shapePreferences)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);
    const maxShapeCount = topShapes[0]?.[1] || 1;
    const topColors = Object.entries(stats.colorPreferences)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);
    const maxColorCount = topColors[0]?.[1] || 1;
    const topClarities = Object.entries(stats.clarityPreferences)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 5);
    const maxClarityCount = topClarities[0]?.[1] || 1;
    const selectedDealer = dealers.find(d => d.id === selectedDealerId);
    return (_jsxs(PageContainer, { children: [_jsxs(PageTitle, { children: [_jsx(FaChartLine, { style: { marginRight: '12px' } }), "Dealer Performance Analytics"] }), _jsx(Text, { children: "Track individual dealer performance, preferences, and conversion metrics" }), isAdmin && (_jsxs(DealerSelector, { id: "tour-dealer-selector", children: [_jsxs("label", { children: [_jsx(FaUser, {}), " Select Dealer:"] }), _jsxs("select", { value: selectedDealerId, onChange: (e) => setSelectedDealerId(e.target.value), children: [_jsx("option", { value: "", children: "-- Choose a dealer --" }), dealers.map(dealer => (_jsxs("option", { value: dealer.id, children: [dealer.name, " (", dealer.email, ")"] }, dealer.id)))] })] })), loading && (_jsx(Text, { style: { textAlign: 'center', padding: '40px' }, children: "Loading analytics..." })), !loading && selectedDealerId && (_jsxs(_Fragment, { children: [_jsxs(ContentSection, { children: [_jsxs(SectionTitle, { children: ["Performance Overview", selectedDealer && ` - ${selectedDealer.name}`] }), _jsxs(StatsGrid, { id: "tour-performance-metrics", children: [_jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaFileContract, { style: { color: '#3b82f6' } }), stats.totalMemos] }), _jsx("div", { className: "stat-label", children: "Total Memo Requests" })] }), _jsxs(StatCard, { "$highlight": stats.approvalRate >= 80, children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaCheckCircle, { style: { color: '#10b981' } }), stats.approvalRate.toFixed(1), "%"] }), _jsx("div", { className: "stat-label", children: "Approval Rate" }), _jsxs("div", { className: "stat-sublabel", children: [stats.approvedMemos, " approved / ", stats.rejectedMemos, " rejected"] })] }), _jsxs(StatCard, { "$highlight": stats.conversionRate >= 30, children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaTrophy, { style: { color: '#f59e0b' } }), stats.conversionRate.toFixed(1), "%"] }), _jsx("div", { className: "stat-label", children: "Conversion Rate" }), _jsxs("div", { className: "stat-sublabel", children: [stats.soldMemos, " sold / ", stats.returnedMemos, " returned"] })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaDollarSign, { style: { color: '#10b981' } }), "$", (stats.avgMemoValue / 1000).toFixed(1), "K"] }), _jsx("div", { className: "stat-label", children: "Avg Memo Value" }), _jsxs("div", { className: "stat-sublabel", children: ["$", (stats.totalValue / 1000000).toFixed(2), "M total"] })] }), _jsxs(StatCard, { children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaClock, { style: { color: '#8b5cf6' } }), stats.avgResponseTime.toFixed(1)] }), _jsx("div", { className: "stat-label", children: "Avg Days to Close" }), _jsx("div", { className: "stat-sublabel", children: "From approval to return/sale" })] }), _jsxs(StatCard, { "$highlight": stats.overdueCount > 0, children: [_jsxs("div", { className: "stat-value", children: [_jsx(FaTimesCircle, { style: { color: stats.overdueCount > 0 ? '#ef4444' : '#22c55e' } }), stats.overdueCount] }), _jsx("div", { className: "stat-label", children: "Overdue Memos" }), _jsx("div", { className: "stat-sublabel", children: stats.overdueCount > 0 ? 'Needs attention' : 'All on time' })] })] })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Diamond Shape Preferences" }), _jsx(ChartCard, { id: "tour-shape-preferences", children: topShapes.length > 0 ? (_jsx(BarChart, { children: topShapes.map(([shape, count]) => (_jsxs(Bar, { "$percentage": (count / maxShapeCount) * 100, "$color": "#3b82f6", children: [_jsx("div", { className: "label", children: shape }), _jsx("div", { className: "bar-container", children: _jsxs("div", { className: "bar-fill", children: [count, " requests"] }) }), _jsxs("div", { className: "count", children: [((count / stats.totalMemos) * 100).toFixed(1), "%"] })] }, shape))) })) : (_jsx(Text, { style: { textAlign: 'center', color: 'var(--text-secondary)' }, children: "No memo requests yet" })) })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Color Preferences" }), _jsx(ChartCard, { id: "tour-color-preferences", children: topColors.length > 0 ? (_jsx(BarChart, { children: topColors.map(([color, count]) => (_jsxs(Bar, { "$percentage": (count / maxColorCount) * 100, "$color": "#10b981", children: [_jsxs("div", { className: "label", children: ["Color ", color] }), _jsx("div", { className: "bar-container", children: _jsxs("div", { className: "bar-fill", children: [count, " requests"] }) }), _jsxs("div", { className: "count", children: [((count / stats.totalMemos) * 100).toFixed(1), "%"] })] }, color))) })) : (_jsx(Text, { style: { textAlign: 'center', color: 'var(--text-secondary)' }, children: "No memo requests yet" })) })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Clarity Preferences" }), _jsx(ChartCard, { children: topClarities.length > 0 ? (_jsx(BarChart, { children: topClarities.map(([clarity, count]) => (_jsxs(Bar, { "$percentage": (count / maxClarityCount) * 100, "$color": "#8b5cf6", children: [_jsx("div", { className: "label", children: clarity }), _jsx("div", { className: "bar-container", children: _jsxs("div", { className: "bar-fill", children: [count, " requests"] }) }), _jsxs("div", { className: "count", children: [((count / stats.totalMemos) * 100).toFixed(1), "%"] })] }, clarity))) })) : (_jsx(Text, { style: { textAlign: 'center', color: 'var(--text-secondary)' }, children: "No memo requests yet" })) })] }), _jsxs(ContentSection, { children: [_jsx(SectionTitle, { children: "Recent Activity" }), _jsx(TimelineCard, { id: "tour-activity-timeline", children: stats.recentActivity.length > 0 ? (stats.recentActivity.map((activity, index) => (_jsxs(TimelineItem, { children: [_jsx("div", { className: "timeline-date", children: new Date(activity.date).toLocaleDateString('en-US', {
                                                month: 'short',
                                                day: 'numeric',
                                                year: 'numeric'
                                            }) }), _jsxs("div", { className: "timeline-text", children: [activity.action.toUpperCase(), " - ", activity.diamond] }), _jsxs("div", { className: "timeline-detail", children: ["Value: $", activity.value.toLocaleString()] })] }, index)))) : (_jsx(Text, { style: { textAlign: 'center', color: 'var(--text-secondary)', padding: '40px' }, children: "No activity recorded yet" })) })] })] })), !loading && !selectedDealerId && (_jsx(Text, { style: { textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }, children: "Select a dealer to view their performance analytics" }))] }));
};
export default DealerAnalytics;
